<!-- Template for a new page -->
<template>
	<div class="d-flex flex-column flex h-100">
		<navbar></navbar>
		<div class="app">
			<reporting-sidenav></reporting-sidenav>
			<div id="content" class="app-content box-shadow-3 d-flex flex flex-column scroll-x">
				<!-- Optional navbar at top of page -->
				<div class="navbar navbar-light bg-light b-b flex-nowrap flex-fixed">
					<div class="navbar-text nav-title flex" id="pageTitle">
						Reporting
						<span class="far fa-fw fa-angle-right"></span>Assessment Stats
					</div>
				</div>

				<div class="flex scroll-x scroll-y">
					<div class="row mx-0 flex-fixed px-3 pt-3 box">
						<div class="col-12 col-sm-6 col-lg-4">
							<div class="form-group">
								<label>Content Area</label>
								<config-select
									:nullOption="true"
									nullText="Any"
									:options="contentAreas"
									v-model="selectedCA"
								></config-select>
							</div>
						</div>
						<div class="col-12 col-sm-6 col-lg-4">
							<div class="form-group">
								<label>Grade</label>
								<config-select
									:nullOption="true"
									nullText="Any"
									sortBy="sequence"
									:options="grades"
									v-model="selectedGrade"
								></config-select>
							</div>
						</div>
						<div class="col-12 col-sm-6 col-lg-4">
							<div class="form-group">
								<label>Assessment</label>
								<config-select
									:nullOption="true"
									nullText="None"
									:options="filteredAssessments"
									v-model="selectedAsmt"
								></config-select>
							</div>
						</div>

						<div class="col-12 col-sm-4">
							<div class="form-group">
								<label>Score Comparison</label>
								<v-select :options="compTypes" v-model="selectedComp" class="noclear"></v-select>
							</div>
						</div>

						<div class="col-12 col-sm-8 d-flex flex-row align-items-end justify-content-end">
							<div class="d-flex flex-row align-items-center form-group">
								<div class="py-0 ml-1">
									<button
										@click="getStats"
										:disabled="!canRun"
										class="btn btn-sm btn-block flex-fixed"
										:class="{ [canRun ? 'theme-accent' : 'btn-secondary']: true }"
									>
										<i v-if="!running" class="far fa-clipboard-list"></i>
										<loading v-if="running" type="icon" />
										<span class="hidden-folded d-inline">&nbsp;Run Report</span>
									</button>
								</div>
							</div>
						</div>
					</div>

					<div v-if="rubric" class="d-flex flex-row box-shadow-3 mb-3">
						<div class="flex">
							<div class="card border-0 p-3">
								<div class="row">
									<div v-if="anyComps" class="col-12 col-sm-6">
										<table>
											<tr>
												<td class="pr-3">
													<h5>Score IRR:</h5>
												</td>
												<td>
													<h5
														:class="percentColor(overallResponseAgreement, 40, 50)"
														v-if="anyComps"
													>
														{{ overallResponseAgreement.toFixed(2) }}%
													</h5>
													<h6 class="text-muted" v-if="!anyComps">None Found</h6>
												</td>
											</tr>
											<tr>
												<td class="pr-3">
													<h5>Trait IRR:</h5>
												</td>
												<td>
													<h5
														:class="percentColor(overallTraitAgreement, 50, 65)"
														v-if="anyComps"
													>
														{{ overallTraitAgreement.toFixed(2) }}%
													</h5>
													<h6 class="text-muted" v-if="!anyComps">None Found</h6>
												</td>
											</tr>
										</table>
									</div>
									<div v-if="anyScores" class="col-12 col-sm-6 d-flex flex-column">
										<div class="flex-grow"></div>
										<div class="flex-fixed">
											<h5 style="display: inline-block">Frequency Distribution</h5>
											<h6 style="display: inline-block" class="text-muted">({{ fdName }})</h6>
										</div>
									</div>
									<h1 v-if="!anyScores" class="w-100 text-very-muted text-center">No Results</h1>
								</div>

								<hr v-if="anyScores" />

								<template v-for="trait in rubric.traits">
									<div class="row mx-0" v-if="anyScores && trait.separator" :key="trait.id">
										<h5 class="col-12 b-b pb-2 px-2 mb-2 text-muted">{{ trait.name }}</h5>
									</div>
									<div class="row" v-if="anyScores && !trait.separator" :key="trait.id">
										<div class="_600 col-12 px-4">{{ trait.name }}</div>
										<div class="col-4 col-sm-2 align-self-center text-center pb-3">
											<table class="table table-borderless is_table">
												<tbody v-if="anyComps">
													<tr>
														<td class="text-center">
															<h1>{{ trait.agreement }}%</h1>
															<h6 class="text-muted">Agreement</h6>
														</td>
													</tr>
													<!--	<tr>
														<td>Kappa</td>
														<td>{{trait.kappa}}</td>
													</tr>
													<tr>
														<td>LWK</td>
														<td>{{trait.lwk}}</td>
													</tr>
													<tr>
														<td>QWK</td>
														<td>{{trait.qwk}}</td>
													</tr> -->
												</tbody>
											</table>
										</div>
										<div v-if="anyComps" class="col-2 col-sm-1"></div>
										<div v-if="anyComps" class="col-6 col-sm-3">
											<table class="confusion-matrix bordered center position-relative mt-4 ml-4">
												<div class="text-center matrix-axis-left">{{ yAxisName }}</div>
												<div class="text-center matrix-axis-top">{{ xAxisName }}</div>
												<tbody>
													<tr v-for="(row, i) in trait.irrDataMatrix" :key="i" v-if="i == 0">
														<td v-for="(col, j) in row" :key="j">{{ col.value }}</td>
													</tr>
													<tr v-for="(row, i) in trait.irrDataMatrix" :key="i" v-if="i != 0">
														<td v-if="j == 0" v-for="(col, j) in row" :key="j">
															{{ col.value }}
														</td>
														<td
															v-if="j != 0"
															:class="col.class"
															@click="matClick(trait.id, col.leftScore, col.rightScore)"
															v-for="(col, j) in row"
															:key="j"
														>
															{{ col.value }}
														</td>
													</tr>
												</tbody>
											</table>
										</div>
										<div class="col-12" :class="{ 'col-sm-6': anyComps, 'px-5': !anyComps }">
											<canvas
												v-if="!running"
												v-chartjs="trait.chartData"
												style="width:100%; height: 250px;"
											></canvas>
										</div>
									</div>
								</template>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<mzfooter></mzfooter>
	</div>
</template>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.ar {
	text-align: center;
}
.d-long {
	display: none;
}
.d-abr {
	display: table-cell;
}
@media (min-width: 1100px) {
	.d-long {
		display: table-cell;
	}
	.d-abr {
		display: none;
	}
}
.t-margin {
	width: 15px;
}
.matrix-axis-top {
	position: absolute;
	left: 0px;
	right: 0px;
	top: -24px;
}
.matrix-axis-left {
	position: absolute;
	right: calc(50% + 12px);
	width: 100%;
	top: 50%;
	transform: rotate(270deg);
	transform-origin: 50% 50%;
}
h5,
h6 {
	margin-bottom: 0;
}
.hover-darken {
	cursor: pointer;
	transition: background-color 0.25s;
}
.hover-darken:hover {
	background-color: rgba(0, 0, 0, 0.1);
}
.table-borderless td,
.table-borderless th {
	border: 0;
}
</style>

<script>
//Directives
require("@/directives/chartjs");

//Services
import ReportingService from "@/services/ReportingService";
import ConfigService from "@/services/ConfigService";
import notie from "@/services/NotieService";
import store from "@/services/Store";
import moment from "moment";
import BB from "bluebird";
import ChartDataLabels from "chartjs-plugin-datalabels";

import ConfigSelect from "@/components/ConfigSelect";

export default {
	name: "ReportAssessmentSummary",

	props: ["user"],

	components: { ConfigSelect },

	data() {
		return {
			chartData: null,
			overallResponseAgreement: 0,
			overallTraitAgreement: 0,
			fdName: "",
			xAxisName: "",
			yAxisName: "",
			rubric: null,
			fd_data: null,

			// scoreTypesLeftAvail: [
			// 	{
			// 		state: 12,
			// 		label: "1st or 2nd"
			// 	},
			// 	{
			// 		state: 3,
			// 		label: "Resolution"
			// 	},
			// 	{
			// 		state: 4,
			// 		label: "Backread"
			// 	}
			// ],
			// scoreTypesRightAvail: [
			// 	{
			// 		state: 12,
			// 		label: "1st or 2nd"
			// 	},
			// 	{
			// 		state: 3,
			// 		label: "Resolution"
			// 	},
			// 	{
			// 		state: 4,
			// 		label: "Backread"
			// 	}
			// ],
			// scoreTypesLeft: [],
			// scoreTypesRight: [],

			// userLeft: null,
			// userRight: null,

			compTypes: [
				{
					label: "1st vs. 2nd",
					leftStates: [12],
					rightStates: [12],
					id: "1v2",
					fdName: "Pool",
					xAxisLabel: "Second Read",
					yAxisLabel: "First Read",
					xAxisWithUserLabel: "Other Read",
					yAxisWithUserLabel: "This User"
				},
				{
					label: "1st vs. Resolution",
					leftStates: [1],
					rightStates: [3],
					id: "1v3",
					fdName: "Resolution",
					xAxisLabel: "Resolution",
					yAxisLabel: "First Read",
					xAxisWithUserLabel: "Resolution",
					yAxisWithUserLabel: "This User"
				},
				{
					label: "2nd vs. Resolution",
					leftStates: [2],
					rightStates: [3],
					id: "2v3",
					fdName: "Resolution",
					xAxisLabel: "Resolution",
					yAxisLabel: "Second Read",
					xAxisWithUserLabel: "Resolution",
					yAxisWithUserLabel: "This User"
				}
			],
			selectedComp: null,

			reportTotals: [],
			reportStats: [],

			contentAreas: [],
			selectedCA: null,

			grades: [],
			selectedGrade: null,

			assessments: [],
			selectedAsmt: null,

			byRubric: false,

			running: false,
			loading: true
		};
	},

	created() {
		this.storagePrefix = "asmtSummary.";
		this.selectedComp = store.bindFromList(this, this.compTypes, "id", 0, "selectedComp");

		BB.props({
			contentAreas: ConfigService.listContentAreas(),
			grades: ConfigService.listGrades(),
			assessments: ConfigService.listAssessments()
		})
			.then(resps => {
				this.contentAreas = resps.contentAreas.data.content_areas;
				this.grades = resps.grades.data;
				this.assessments = resps.assessments.data.assessments;

				this.selectedCA = store.bindFromList(this, this.contentAreas, "id", null, "selectedCA");
				this.selectedGrade = store.bindFromList(this, this.grades, "id", null, "selectedGrade");
				this.selectedAsmt = store.bindFromList(this, this.assessments, "id", null, "selectedAsmt");
			})
			.catch(err => {
				console.log(err);
				notie.error("Failed to load config options", err);
			})
			.finally(() => {
				this.loading = false;
			});
	},

	computed: {
		anyScores() {
			if (!this.rubric || !this.fd_data) {
				return false;
			}

			return this.fd_data.length > 0;
		},

		anyComps() {
			if (!this.rubric) {
				return false;
			}
			var result = _.find(this.rubric.traits, trait => {
				if (trait.agreement != "NaN") {
					return true;
				}
			});
			return result ? true : false;
		},

		canRun() {
			return this.selectedAsmt && this.selectedAsmt.id;
		},

		filteredAssessments() {
			if (!this.selectedCA && !this.selectedGrade) {
				return this.assessments;
			}
			return _.filter(this.assessments, asmt => {
				let matchedCA = true;
				if (this.selectedCA) {
					matchedCA = this.selectedCA.id == asmt.content_area_id;
				}
				let matchedGrade = true;
				if (this.selectedGrade) {
					matchedGrade = false;
					_.each(asmt.grade_ids, gid => {
						if (this.selectedGrade.id == gid) {
							matchedGrade = true;
						}
					});
				}

				return matchedCA && matchedGrade;
			});
		}
	},

	watch: {},

	methods: {
		getStats() {
			this.running = true;
			var asmt = this.selectedAsmt;
			var userLeft = { id: null };
			//Server supports a second user for comparison, but client currently does not
			//var userRight = this.userRight;
			var userRight = { id: null };
			var leftStates = this.selectedComp.leftStates;
			var rightStates = this.selectedComp.rightStates;

			if (!this.canRun) {
				return;
			}

			function getOverallResponseAgreement(agreement) {
				return agreement * 100;
			}

			function getOverallTraitAgreement(stats) {
				// debugger;
				var agreed = 0;
				var total = 0;

				_.each(stats, stat => {
					if (stat.left_score == stat.right_score) {
						agreed += stat.count;
					}
					total += stat.count;
				});

				return (agreed / total) * 100;
			}

			function getTraitAgreement(stats, traitId) {
				var agreed = 0;
				var total = 0;

				_.each(stats, stat => {
					if (stat.trait_id == traitId) {
						if (stat.left_score == stat.right_score) {
							agreed += stat.count;
						}
						total += stat.count;
					}
				});

				return ((agreed / total) * 100).toFixed(2);
			}

			function getFd(stats, traitId, sp, field) {
				var count = 0;
				var total = 0;
				_.each(stats, stat => {
					if (stat.trait_id == traitId) {
						total += stat[field];
						if (stat.score == sp) {
							count += stat[field];
						}
					}
				});
				if (total > 0) {
					return {
						label: sp,
						value: (count / total) * 100,
						count: count,
						total: total
					};
				} else {
					return {
						none: true
					};
				}
			}

			function getStatValue(stats, trait, firstScore, secondScore) {
				var count = 0;

				_.each(stats, function(stat) {
					if (stat.trait_id == trait.id && stat.left_score == firstScore && stat.right_score == secondScore) {
						count = stat.count;
					}
				});
				return count;
			}

			this.data = [
				{
					values: [
						{ label: "1", value: 10, color: "#009688" },
						{ label: "2", value: 30, color: "#009688" },
						{ label: "3", value: 40, color: "#009688" },
						{ label: "4", value: 10, color: "#009688" }
					]
				}
			];

			ReportingService.getAssessmentSummaryStats(
				this.selectedAsmt.id,
				leftStates,
				userLeft.id,
				rightStates,
				userRight.id
			)
				.then(resp => {
					let data = resp.data;

					this.running = false;
					this.rubric = data.rubric;
					this.fd_data = data.fd_data;
					var rubric = this.rubric;

					//debugger;
					this.overallResponseAgreement = getOverallResponseAgreement(data.overall_response_agreement);
					this.overallTraitAgreement = getOverallTraitAgreement(data.matrix_data);
					this.fdName = this.selectedComp.fdName;
					this.xAxisName = this.selectedComp.xAxisLabel;
					this.yAxisName = this.selectedComp.yAxisLabel;

					rubric.traits = _.sortBy(rubric.traits, t => {
						return t.sequence;
					});

					_.each(rubric.traits, trait => {
						trait.chartData = {
							type: "bar",
							plugins: [ChartDataLabels],
							data: {
								labels: [],
								datasets: [
									{
										label: "1st/2nd",
										fullName: "First & Second",
										xAxisID: "sp",
										yAxisID: "freq",
										backgroundColor: "rgba(0, 188, 212, 0.2)",
										borderColor: "rgba(0, 188, 212, 0.5)",
										borderWidth: 1,
										// hoverBackgroundColor:
										// 	"rgba(0, 188, 212, 0.3)",
										// hoverBorderColor:
										// 	"rgba(0, 188, 212, 1)",
										// hoverBorderWidth: 1,
										data: [],
										counts: []
									}
								]
							},
							options: {
								plugins: {
									datalabels: {
										formatter: function(value, context) {
											return value + "%";
										},
										anchor: "end",
										align: "end"
									}
								},
								tooltips: {
									mode: "point",
									displayColors: false,
									callbacks: {
										title: (items, data) => {
											let i = items[0].datasetIndex;
											let sp = items[0].xLabel;
											let fullName = data.datasets[i].fullName;
											return `${fullName}: ${sp}`;
										},
										label: (item, data) => {
											let i = item.datasetIndex;
											let j = item.index;
											let count = data.datasets[i].counts[j].count;
											let total = data.datasets[i].counts[j].total;
											let per = data.datasets[i].data[j];
											return `${count} out of ${total} (${per}%)`;
										}
									}
								},
								maintainAspectRatio: false,
								legend: {
									display: true
								},
								scales: {
									xAxes: [
										{
											id: "sp",
											scaleLabel: {
												display: true,
												labelString: "Score Point"
											}
										}
									],
									yAxes: [
										{
											id: "freq",
											scaleLabel: {
												display: true,
												labelString: "Frequency %"
											},
											ticks: {
												suggestedMin: 0,
												suggestedMax: 25
											}
										}
									]
								}
							}
						};
						trait.agreement = getTraitAgreement(data.matrix_data, trait.id);
						//Create empty matrix padded with scorepoint and FD data
						var sps = [];
						var firstRow = [" "];

						var step = 1;
						if (trait.use_half_points) {
							step = 0.5;
						}

						for (var i = trait.min; i <= trait.max; i += step) {
							firstRow.push({ value: i });
							sps.push(i);
						}

						//trait.chartData.data.datasets[0].data = [];
						//trait.chartData.data.labels = [];
						let resData = [];
						let resCounts = [];
						let brData = [];
						let brCounts = [];
						var irrDataMatrix = [firstRow];
						_.each(sps, (sp, i) => {
							var live = getFd(data.fd_data, trait.id, sp, "count");
							var res = getFd(data.fd_data, trait.id, sp, "res_count");
							var br = getFd(data.fd_data, trait.id, sp, "br_count");
							trait.chartData.data.datasets[0].data[i] = +(Math.round(live.value + "e+2") + "e-2");
							trait.chartData.data.datasets[0].counts[i] = live;
							if (!res.none) {
								resData[i] = +(Math.round(res.value + "e+2") + "e-2");
								resCounts[i] = res;
							}
							if (!br.none) {
								brData[i] = +(Math.round(br.value + "e+2") + "e-2");
								brCounts[i] = br;
							}
							trait.chartData.data.labels[i] = live.label;

							//Push new row
							var row = [{ value: sp }];
							_.each(sps, sp2 => {
								var value = getStatValue(data.matrix_data, trait, sp, sp2);
								var classa = {};
								if (value > 0) {
									if (Math.abs(sp - sp2) == 0) {
										classa = { cmgreen: true };
									} else if (!trait.use_half_points && Math.abs(sp - sp2) <= 1) {
										classa = { cmorange: true };
									} else if (trait.use_half_points && Math.abs(sp - sp2) <= 0.5) {
										classa = { cmorange: true };
									} else {
										classa = { cmred: true };
									}
								}

								if (this.user.role.page_auditing) {
									classa["hover-darken"] = true;
								}

								row.push({
									value: value,
									class: classa,
									traitid: trait.id,
									leftScore: sp,
									rightScore: sp2
								});
							});
							irrDataMatrix.push(row);
						});
						if (resData.length > 0) {
							trait.chartData.data.datasets.push({
								label: "Res",
								fullName: "Resolution",
								xAxisID: "sp",
								yAxisID: "freq",
								backgroundColor: "rgba(92, 199, 50, 0.2)",
								borderColor: "rgba(92, 199, 50, 0.5)",
								borderWidth: 1,
								// hoverBackgroundColor: "rgba(92, 199, 50, 0.3)",
								// hoverBorderColor: "rgba(92, 199, 50, 1)",
								// hoverBorderWidth: 1,
								data: resData,
								counts: resCounts
							});
						}
						if (brData.length > 0) {
							trait.chartData.data.datasets.push({
								label: "BR",
								fullName: "Backread",
								xAxisID: "sp",
								yAxisID: "freq",
								backgroundColor: "rgba(212, 148, 0, 0.2)",
								borderColor: "rgba(212, 148, 0, 0.5)",
								borderWidth: 1,
								// hoverBackgroundColor: "rgba(212, 148, 0, 0.3)",
								// hoverBorderColor: "rgba(212, 148, 0, 1)",
								// hoverBorderWidth: 1,
								data: brData,
								counts: brCounts
							});
						}
						this.$forceUpdate();

						trait.irrDataMatrix = irrDataMatrix;

						//QWK stuff
						var size = trait.max - trait.min + 1;
						if (trait.use_half_points) {
							size = size * 2 - 1;
						}
						var arr = [];
						for (var i = 0; i < size; i++) {
							arr[i] = [];
						}

						for (var i = 1; i < irrDataMatrix.length; i++) {
							for (var j = 1; j < irrDataMatrix.length; j++) {
								arr[i - 1][j - 1] = irrDataMatrix[i][j].value;
							}
						}

						trait.qwk = this.qwk(arr).toFixed(4);
						trait.lwk = this.lwk(arr).toFixed(4);
						trait.kappa = this.kappa(arr).toFixed(4);

						// var test1 = [
						//     [2, 0, 0],
						//     [0, 2, 1],
						//     [0, 1, 1]
						// ]
						// console.log("TEST " + kappa(test1))
						// console.log("TEST " + lwk(test1))
						// console.log("TEST " + qwk(test1))
					});
				})
				.catch(err => {
					this.running = false;
					notie.error("Failed to load summary", err);
				});
		},

		qwk(conf_mat) {
			var num_ratings = conf_mat.length;
			var num_scored = this.sum(conf_mat, 0);

			var hist_rater_a = this.sum(conf_mat, 1);
			var hist_rater_b = this.sum(conf_mat, 2);

			var numerator = 0.0;
			var denominator = 0.0;

			for (var i = 0; i < conf_mat.length; i++) {
				for (var j = 0; j < conf_mat.length; j++) {
					var expected = (hist_rater_a[i] * hist_rater_b[j]) / num_scored;
					var d = Math.pow(i - j, 2) / Math.pow(num_ratings - 1, 2);
					numerator += (d * conf_mat[i][j]) / num_scored;
					denominator += (d * expected) / num_scored;
				}
			}

			return 1.0 - numerator / denominator;
		},

		lwk(conf_mat) {
			var num_ratings = conf_mat.length;
			var num_scored = this.sum(conf_mat, 0);

			var hist_rater_a = this.sum(conf_mat, 1);
			var hist_rater_b = this.sum(conf_mat, 2);

			var numerator = 0.0;
			var denominator = 0.0;

			for (var i = 0; i < conf_mat.length; i++) {
				for (var j = 0; j < conf_mat.length; j++) {
					var expected = (hist_rater_a[i] * hist_rater_b[j]) / num_scored;
					var d = Math.abs(i - j) / (num_ratings - 1);
					numerator += (d * conf_mat[i][j]) / num_scored;
					denominator += (d * expected) / num_scored;
				}
			}

			return 1.0 - numerator / denominator;
		},

		kappa(conf_mat) {
			var num_ratings = conf_mat.length;
			var num_scored = this.sum(conf_mat, 0);

			var hist_rater_a = this.sum(conf_mat, 1);
			var hist_rater_b = this.sum(conf_mat, 2);

			var numerator = 0.0;
			var denominator = 0.0;

			for (var i = 0; i < conf_mat.length; i++) {
				for (var j = 0; j < conf_mat.length; j++) {
					var expected = (hist_rater_a[i] * hist_rater_b[j]) / num_scored;
					var d = 1.0;
					if (i == j) {
						d = 0.0;
					}
					numerator += (d * conf_mat[i][j]) / num_scored;
					denominator += (d * expected) / num_scored;
				}
			}

			return 1.0 - numerator / denominator;
		},

		sum(matrix, axis) {
			var result = [];
			//Return scalar sum of the matrix
			if (axis == 0) {
				var total = 0;
				for (var i = 0; i < matrix.length; i++) {
					for (var j = 0; j < matrix.length; j++) {
						total += matrix[i][j];
					}
				}
				return total;
			}

			//Calculate sum for each row and return histogram
			if (axis == 1) {
				for (var i = 0; i < matrix.length; i++) {
					var row = matrix[i];
					var total = 0;
					_.each(row, entry => {
						total += entry;
					});
					result[i] = total;
				}
				return result;
			}

			//Calculate sum for each column and return histogram
			if (axis == 2) {
				var mat = Array(matrix.length);
				for (var i = 0; i < matrix.length; i++) {
					mat[i] = Array(matrix.length);
					for (var j = 0; j < matrix.length; j++) {
						mat[i][j] = matrix[j][i];
					}
				}
				return this.sum(mat, 1);
			}
		},

		percentColor(val, t1, t2) {
			return {
				"text-danger": val < t1,
				"text-warning": val >= t1 && val < t2,
				"text-success": val > t2
			};
		},

		matClick(traitId, firstScore, secondScore) {
			if (!this.user.role.page_auditing) {
				return;
			}
			let comp = this.selectedComp;
			if (!(comp && comp.leftStates && comp.leftStates[0] && comp.rightStates && comp.rightStates[0])) {
				return;
			}
			let firstType = comp.leftStates[0] != 12 ? comp.leftStates[0] : 1;
			let secondType = comp.rightStates[0] != 12 ? comp.rightStates[0] : 2;

			var sps = [
				{
					score_type: firstType,
					trait_id: traitId,
					score: firstScore
				},
				{
					score_type: secondType,
					trait_id: traitId,
					score: secondScore
				}
			];

			store.set(this, "audit.r.audit_trait_scores", sps);

			//Set irrelevant things to blank
			store.set(this, "audit.r.selectedFlagType", null);
			store.set(this, "audit.r.selectedFlagReviewType", null);
			store.set(this, "audit.r.selectedState", { value: 0 });
			store.set(this, "audit.r.response_id", "");
			store.set(this, "audit.r.labels", []);
			store.set(this, "audit.r.users", []);

			store.set(this, "audit.r.trait_scores", sps);
			store.set(this, "audit.r.selectedAsmt", this.selectedAsmt);
			store.set(this, "audit.r.score_specific_query", true);
			store.set(this, "audit.r.filter_types", []);

			this.$router.push("/auditing");
		}
	}
};
</script>



// WEBPACK FOOTER //
// src/vues/QualityMgmt/ReportAssessmentSummary.vue